import { useContext } from 'react';
import { Button } from 'reactstrap';
import { jsPDF as JsPDF } from 'jspdf';
import * as turf from '@turf/turf';
import tinycolor from 'tinycolor2';
import { identity } from 'lodash';
import { gql, useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { MapContext } from '../../../contexts/MapContext';
import { DISTANCE_UNITS, feetPerMile } from '../../../constants/units';
import { colorForCrop, cropThemes, getPaintColor } from '../../../utils/colors';
import {
  asDynamicDecimal,
  asQuantityWithUnit,
} from '../../../utils/formatters';
import { useUnits } from '../../../contexts/UnitContext';
import { resetMapLayers } from '../../../utils/map';

// Layer ids
const SCALE_LAYER = 'scale';
const SCALE_LABELS_LAYER = 'scale-labels';
const SCALE_LAYER_BACKGROUND = 'scale-background';

const LABELS_SOURCE = 'labels-all';
const ALL_LABELS_LAYER = 'all-section-labels';

// Constants in mm
const COL_WIDTH = 90;
const DOC_WIDTH = 300;
const MAX_Y = 190;
const PADDING = 8;
const LINE_START_X = PADDING;
const LINE_START_Y = 30; // Accounts for header
const LINE_COLOR = tinycolor('#ccc').toRgb();
const LINE_HEIGHT_Y = 10;
const COMPASS_SIZE = 12;
const MAP_SIZE = DOC_WIDTH - COL_WIDTH;
const HEADER_TEXT_WIDTH =
  COL_WIDTH - PADDING * 2 - COMPASS_SIZE - 2; /* padding */

const ellipsis = '…';

const TeamImageQuery = gql`
  query TeamImageQuery($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      team {
        id
        signedUrl
      }
    }
  }
`;

// Map width thresholds for scale (in feet)
const scaleWidthThresholds = [1000, 8000, Number.MAX_VALUE];
// Corresponding scale widths for each unit
const scaleWidths = {
  ft: [100, 500, 2000],
  m: [30, 150, 600],
};

const fetchCompassImage = async () => {
  try {
    return await fetch('/compass.png')
      .then((r) => r.arrayBuffer())
      .then((r) => new Uint8Array(r));
  } catch (e) {
    console.error(e);
    return null;
  }
};

const fetchTeamImage = async (signedUrl) => {
  if (!signedUrl) return null;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (err) => reject(err));
    img.src = signedUrl;
  })
    .then((img) => ({
      imageData: signedUrl,
      width: img.width,
      height: img.height,
    }))
    .catch((e) => {
      console.error(e);
      return null;
    });
};

// Adds a fixed scale to the top right of the map
const addMapScale = (map, { transformToUnit = identity, displayUnit }) => {
  const scaleWidthsForUnit = scaleWidths[displayUnit];
  const unitsPerMile = transformToUnit(feetPerMile);
  const toGeoJSON = (point) => {
    return turf.point([point.lng, point.lat]);
  };
  const ne = toGeoJSON(map.getBounds().getNorthEast());
  const sw = toGeoJSON(map.getBounds().getSouthWest());
  const diagonal = turf.distance(ne, sw);

  const mapWidthFt =
    turf.distance(
      ne,
      turf.point([sw.geometry.coordinates[0], ne.geometry.coordinates[1]]),
      { units: 'miles' },
    ) * feetPerMile;
  const scaleWidth =
    scaleWidthsForUnit[scaleWidthThresholds.findIndex((t) => mapWidthFt < t)];
  const scaleEnd = turf.along(turf.lineString([ne, sw]), diagonal * 0.05);
  const scaleStart = turf.destination(
    scaleEnd,
    scaleWidth / unitsPerMile,
    -90,
    {
      units: 'miles',
    },
  );
  const scaleFeature = turf.lineString([
    scaleStart.geometry.coordinates,
    scaleEnd.geometry.coordinates,
  ]);
  map.addSource(SCALE_LAYER, {
    type: 'geojson',
    data: scaleFeature,
  });
  const labelData = turf.featureCollection([
    { ...scaleStart, properties: { label: '0' } },
    {
      ...scaleEnd,
      properties: { label: `${scaleWidth}${displayUnit}` },
    },
  ]);
  map.addSource(SCALE_LABELS_LAYER, {
    type: 'geojson',
    data: labelData,
  });
  map.addLayer({
    id: SCALE_LAYER_BACKGROUND,
    source: SCALE_LAYER,
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#000',
      'line-width': 8,
    },
  });
  map.addLayer({
    id: SCALE_LAYER,
    source: SCALE_LAYER,
    type: 'line',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#fff',
      'line-width': 5,
    },
  });

  map.addLayer({
    id: SCALE_LABELS_LAYER,
    source: SCALE_LABELS_LAYER,
    type: 'symbol',
    layout: {
      'text-field': '{label}',
      'symbol-placement': 'point',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-size': 12,
      'text-offset': [0, 1],
    },
    paint: {
      'text-halo-color': 'rgba(0,0,0,1)',
      'text-halo-width': 2,
      'text-color': '#fff',
    },
  });
};

export const groupSectionLabelsByCoordinate = (sections) => {
  const reducedLabels = sections.reduce((labelsMap, section) => {
    if (!section.fieldFeature) return labelsMap;
    const { coordinates } = turf.centerOfMass(section.fieldFeature).geometry;
    // transform coordinates to string for key safety
    const coordinateString = coordinates.toString();
    const labelGroupAtCoordinates = labelsMap[coordinateString];
    if (labelGroupAtCoordinates) {
      labelGroupAtCoordinates.additionalCount += 1;
      const { name, additionalCount } = labelGroupAtCoordinates;
      labelGroupAtCoordinates.labelText = `${name} + ${additionalCount} more`;
    } else {
      const { name } = section;
      // eslint-disable-next-line no-param-reassign
      labelsMap[coordinateString] = {
        name,
        labelText: name,
        coordinates,
        additionalCount: 0,
      };
    }
    return labelsMap;
  }, {});
  return Object.values(reducedLabels);
};

export const addNonOverlappingLabels = (map, groupedLabels) => {
  const labels = turf.featureCollection(
    groupedLabels.map((label) =>
      turf.point(label.coordinates, {
        description: label.labelText,
      }),
    ),
  );

  map.addSource(LABELS_SOURCE, {
    type: 'geojson',
    data: labels,
  });

  map.addLayer({
    id: ALL_LABELS_LAYER,
    type: 'symbol',
    source: LABELS_SOURCE,

    layout: {
      'symbol-placement': 'point',
      'text-field': ['get', 'description'],
      'text-variable-anchor': ['center', 'top', 'bottom', 'left', 'right'],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-radial-offset': 0.5,
      'text-justify': 'auto',
      'text-size': 12,
      'text-offset': [0, 1],
    },
    paint: {
      'text-halo-color': 'rgba(0,0,0,1)',
      'text-halo-width': 2,
      'text-color': '#fff',
    },
  });
};

export const removeOverlappingLabels = (map) => {
  resetMapLayers(map, [ALL_LABELS_LAYER, LABELS_SOURCE]);
};

const removeMapScale = (map) => {
  resetMapLayers(map, [
    SCALE_LAYER,
    SCALE_LAYER_BACKGROUND,
    SCALE_LABELS_LAYER,
  ]);
};

const addDivider = (doc, y) => {
  doc.setLineWidth(0.25);
  doc.setDrawColor(LINE_COLOR.r, LINE_COLOR.g, LINE_COLOR.b);
  doc.line(0, y, COL_WIDTH, y);
};

// See: https://artskydj.github.io/jsPDF/docs/module-split_text_to_size.html#~getStringUnitWidth
const PTS_TO_MM = 25.6 / 72;

// Requires doc to have already set the font size!
// Assumes doc is using the default units (mm) and maxWidth is in mm
const maxWidthEllidedText = (doc, text, maxWidth) => {
  const textWidth = doc.getTextWidth(text);
  if (textWidth <= maxWidth) {
    return text;
  }
  const ellipsisWidth = doc.getTextWidth(ellipsis);
  const charWidthsArray = doc.getCharWidthsArray(text);
  // Find the index of last character that fits
  const maxWithEllipsis = maxWidth - ellipsisWidth;
  const { maxIndex } = charWidthsArray.reduce(
    (acc, charWidth, index) => {
      // charWidth is relative to 1 pt, so multiply by font size to get pts,
      // then convert to mm.
      // See: https://artskydj.github.io/jsPDF/docs/module-split_text_to_size.html#~getStringUnitWidth
      const width = acc.width + charWidth * doc.getFontSize() * PTS_TO_MM;
      return {
        width,
        maxIndex: width > maxWithEllipsis ? acc.maxIndex : index,
      };
    },
    { width: 0, maxIndex: 0 },
  );
  return text.substring(0, maxIndex + 1) + ellipsis;
};

// Requires doc to have already set the font size!
const getSubtitleText = (doc, subtitle, sectionCount) => {
  const sectionQuantity = asQuantityWithUnit(
    'sections',
    'section',
  )(sectionCount);
  const displaySectionQuantity = subtitle
    ? ` (${sectionQuantity})`
    : sectionQuantity;
  const sectionCountWidth = doc.getTextWidth(displaySectionQuantity);
  if (subtitle) {
    const subtitleMaxWidth = HEADER_TEXT_WIDTH - sectionCountWidth;
    const displaySubtitle = maxWidthEllidedText(
      doc,
      subtitle,
      subtitleMaxWidth,
    );
    return `${displaySubtitle}${displaySectionQuantity}`;
  }
  // Don't worry about the size, it should always fit
  return displaySectionQuantity;
};

// Adds the map and header to a new page
const addPageTemplate = (
  doc,
  aspectRatio,
  mapImage,
  compassImage,
  teamImage,
  title,
  subtitle,
  sectionCount,
) => {
  // Add map image
  doc.setFillColor(256, 256, 256);
  doc.rect(COL_WIDTH, 0, DOC_WIDTH, MAP_SIZE, 'F');
  doc.addImage({
    imageData: mapImage,
    x: COL_WIDTH,
    y: 0,
    w: MAP_SIZE,
    h: MAP_SIZE * aspectRatio,
  });
  // Add compass
  if (compassImage) {
    doc.addImage({
      imageData: compassImage,
      x: COL_WIDTH + PADDING / 2,
      y: 4,
      w: COMPASS_SIZE,
      h: COMPASS_SIZE,
    });
  }
  // Add team image
  if (teamImage) {
    const { imageData, height, width } = teamImage;
    doc.addImage({
      imageData,
      x: COL_WIDTH - PADDING - COMPASS_SIZE,
      y: 4,
      w: height
        ? // Keep width proportional to size of original image, but crop if
          // needed so it does not overflow available space
          Math.min((width * COMPASS_SIZE) / height, COMPASS_SIZE + PADDING)
        : COMPASS_SIZE, // Avoid division by zero
      h: COMPASS_SIZE,
    });
  }
  // Add title
  doc.setFontSize(14);
  doc.text(
    maxWidthEllidedText(
      doc,
      title,
      teamImage ? HEADER_TEXT_WIDTH : HEADER_TEXT_WIDTH + COMPASS_SIZE,
    ),
    LINE_START_X,
    9.5,
  );

  // Add subtitle and section count
  doc.setFontSize(10);
  doc.text(getSubtitleText(doc, subtitle, sectionCount), LINE_START_X, 14.5);
  addDivider(doc, 20);
  doc.setFontSize(12);
};

const addCropsHeader = (doc) => {
  doc.setFont('helvetica', 'bold');
  doc.text('Crops', LINE_START_X, LINE_START_Y);
};

const addSwatch = (doc, y, color, borderColor /* Optional */) => {
  const fillColor = tinycolor(color).toRgb();
  const drawColor = tinycolor(borderColor || color).toRgb();
  doc.setFillColor(fillColor.r, fillColor.g, fillColor.b);
  doc.setDrawColor(drawColor.r, drawColor.g, drawColor.b);
  doc.roundedRect(LINE_START_X, y - 3.4, 4, 4, 1, 1, 'FD');
};

const addCrop = (doc, y, { crop, treeCount, acreage }) => {
  addSwatch(
    doc,
    y,
    colorForCrop(crop, cropThemes.LIGHT),
    colorForCrop(crop, cropThemes.DARK),
  );
  doc.setFont('helvetica', 'bold');
  doc.text(crop.name, LINE_START_X + PADDING, y);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `${treeCount} trees | ${asDynamicDecimal(acreage)} acres`,
    LINE_START_X + PADDING,
    y + PADDING,
  );
};

const addLayerInfo = (doc, y, layer) => {
  const color = getPaintColor(layer);
  addSwatch(doc, y, color);
  doc.setFont('helvetica', 'normal');
  doc.text(layer.displayName, LINE_START_X + PADDING, y);
};

const ExportPdfButton = ({ cropData, title, subtitle, sections, ...props }) => {
  const unit = useUnits(DISTANCE_UNITS);
  const {
    layers: { sortedLayers },
    mapState: { mapRef, setContext },
    fields: { fieldSections },
  } = useContext(MapContext);
  const { farmId } = useParams();
  const { data: teamImageData } = useQuery(TeamImageQuery, {
    variables: { farmId },
  });

  const resetMap = () => {
    const map = mapRef.current;
    removeMapScale(map);
    removeOverlappingLabels(map);
    setContext({});
  };

  const generateAndExport = async () => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    // Show all section names and outlines when we are
    // exporting all sections. Names and outlines will already be
    // shown for a selected subset of sections.
    if (sections.length === fieldSections.length) {
      setContext({ selectAllSections: true, hideIndividualLabels: true });
    } else {
      setContext({ hideIndividualLabels: true });
    }
    // Reset pitch and bearing so the map is north up
    map.jumpTo({ bearing: 0, pitch: 0 });
    addMapScale(map, unit);
    const sectionLabels = groupSectionLabelsByCoordinate(sections);
    addNonOverlappingLabels(map, sectionLabels);

    const compassImage = await fetchCompassImage();
    if (!compassImage) throw new Error('Failed to fetch compass image');

    const teamImage = await fetchTeamImage(
      teamImageData?.farm?.team?.signedUrl,
    );

    const exportPdf = () => {
      // Get map image
      const canvas = map.getCanvas();
      const { width, height } = canvas.getBoundingClientRect();
      const aspectRatio = height / width;
      const mapImage = canvas.toDataURL();

      const layersWithData = sortedLayers.filter(
        ({ data, shown }) => data?.features.length > 0 && shown,
      );

      const setupPage = (doc) => {
        addPageTemplate(
          doc,
          aspectRatio,
          mapImage,
          compassImage,
          teamImage,
          title,
          subtitle,
          sections.length,
        );
      };

      const doc = new JsPDF('landscape');
      setupPage(doc);
      if (cropData.length > 0) {
        addCropsHeader(doc);
      }
      let lineStartY = LINE_START_Y + LINE_HEIGHT_Y; // Account for Crops header
      cropData.forEach((data) => {
        if (lineStartY > MAX_Y) {
          doc.addPage();
          setupPage(doc);
          addCropsHeader(doc);
          lineStartY = LINE_START_Y + LINE_HEIGHT_Y; // Account for Crops header
        }
        addCrop(doc, lineStartY, data);
        lineStartY += 2 * LINE_HEIGHT_Y;
      });
      if (
        lineStartY <= MAX_Y &&
        cropData.length > 0 &&
        layersWithData.length > 0
      ) {
        // Add a divider if we didn't fill the page
        addDivider(doc, lineStartY);
        lineStartY += LINE_HEIGHT_Y;
      }
      layersWithData.forEach((layer) => {
        if (lineStartY > MAX_Y) {
          doc.addPage();
          setupPage(doc);
          lineStartY = LINE_START_Y;
        }
        addLayerInfo(doc, lineStartY, layer);
        lineStartY += LINE_HEIGHT_Y;
      });

      window.open(doc.output('bloburl'), '_blank');
      resetMap();
    };

    const exportAfterRedraw = () => {
      // Wait 250ms then retry if the scale or label layer isn't loaded yet
      if (
        !map.isSourceLoaded(SCALE_LAYER) ||
        !map.isSourceLoaded(SCALE_LABELS_LAYER) ||
        !map.isSourceLoaded(LABELS_SOURCE)
      ) {
        setTimeout(exportAfterRedraw, 250);
        return;
      }

      // Wait an additional 500ms for layer painting to finish
      // (eg. section names and scale)
      setTimeout(exportPdf, 500);
    };

    exportAfterRedraw();
  };

  const onClick = () =>
    generateAndExport().catch((e) => {
      console.error(e);
      resetMap();
    });

  return <Button onClick={onClick} {...props} />;
};

export default ExportPdfButton;
